import { getPublicationRefreshMessage } from '../../i18n/publicationRefreshMessages';

export const NO_PUBLICATION = 'NO_PUBLICATION';
export const PUBLICATION_REMOVE_UI = 'DEFERRED';

export const PUBLICATION_STATUSES = Object.freeze(['DRAFT', 'PUBLISHED', 'UNPUBLISHED', 'REMOVED']);
export const PUBLICATION_VISIBILITIES = Object.freeze(['PUBLIC', 'FOLLOWERS', 'PRIVATE']);

const KNOWN_STATUSES = new Set(PUBLICATION_STATUSES);
const KNOWN_VISIBILITIES = new Set(PUBLICATION_VISIBILITIES);

export const PUBLISH_CONFIRM_COPY = {
  title: '確認發布這趟行程？',
  body: '發布後，符合可見對象的旅人可以查看這趟行程。',
};

export const CUSTOM_LOCATION_NOT_PUBLIC_MESSAGE =
  '此行程包含尚未公開的自訂地點，請先將地點設為公開或更換地點。';

export const REPUBLISH_CONFIRM_COPY = {
  title: '重新發布這趟行程？',
  body: '重新發布後，其他人會看到你目前編輯中的最新內容。',
};

export const UNPUBLISH_CONFIRM_COPY = {
  title: '確認停止分享？',
  body: '公開頁面將暫時無法查看。已分享的卡片可能顯示無法查看。之後重新公開仍使用同一個公開連結。',
};

/** Product-facing visibility labels (UX-03-C). Domain enums stay internal. */
export const PUBLICATION_VISIBILITY_PRODUCT_LABELS = Object.freeze({
  PRIVATE: '僅自己',
  FOLLOWERS: '追蹤者',
  PUBLIC: '所有人',
});

export const PUBLICATION_VISIBILITY_HELP_TEXT = Object.freeze({
  PRIVATE: '只有自己可查看',
  FOLLOWERS: '允許符合既有追蹤者規則的使用者查看',
  PUBLIC: '公開分享',
});

export function publicationVisibilityProductLabel(visibility) {
  const key = String(visibility || '');
  return PUBLICATION_VISIBILITY_PRODUCT_LABELS[key] || '';
}

export function publicationVisibilityHelpText(visibility) {
  const key = String(visibility || '');
  return PUBLICATION_VISIBILITY_HELP_TEXT[key] || '';
}

function snowflakeId(value) {
  if (value == null || value === '') return null;
  return String(value);
}

function normalizeDirtyState(status, value) {
  if (status !== 'PUBLISHED') return null;
  const raw = value == null || value === '' ? 'UNKNOWN' : String(value);
  if (raw === 'UNCHANGED' || raw === 'CHANGED' || raw === 'UNKNOWN') return raw;
  return 'UNKNOWN';
}

export function emptyPublication(journeyId = null) {
  return {
    presence: NO_PUBLICATION,
    publicationId: null,
    journeyId: journeyId == null ? null : String(journeyId),
    ownerMemberId: null,
    creatorMemberId: null,
    status: null,
    visibility: null,
    allowCopy: false,
    allowPromotion: false,
    publishedAt: null,
    unpublishedAt: null,
    removedAt: null,
    rowVersion: null,
    createTime: null,
    updateTime: null,
    statusKnown: true,
    visibilityKnown: true,
    updateAvailable: false,
    dirtyState: null,
    refreshAllowed: false,
    blockingReasons: [],
    noOp: false,
    hashVersion: null,
    lastMaterializedAt: null,
  };
}

export function normalizeJourneyPublication(value, journeyId = null) {
  if (value == null) return emptyPublication(journeyId);
  const status = value.status == null || value.status === '' ? null : String(value.status);
  const visibility = value.visibility == null || value.visibility === '' ? null : String(value.visibility);
  return {
    presence: 'PRESENT',
    publicationId: snowflakeId(value.publicationId),
    journeyId: snowflakeId(value.journeyId) || (journeyId == null ? null : String(journeyId)),
    ownerMemberId: snowflakeId(value.ownerMemberId),
    creatorMemberId: snowflakeId(value.creatorMemberId),
    status,
    visibility,
    allowCopy: Boolean(value.allowCopy),
    allowPromotion: Boolean(value.allowPromotion),
    publishedAt: value.publishedAt || null,
    unpublishedAt: value.unpublishedAt || null,
    removedAt: value.removedAt || null,
    rowVersion: value.rowVersion == null || value.rowVersion === '' ? null : Number(value.rowVersion),
    createTime: value.createTime || null,
    updateTime: value.updateTime || null,
    statusKnown: status == null || KNOWN_STATUSES.has(status),
    visibilityKnown: visibility == null || KNOWN_VISIBILITIES.has(visibility),
    updateAvailable: Boolean(value.updateAvailable),
    dirtyState: normalizeDirtyState(status, value.dirtyState),
    refreshAllowed: value.refreshAllowed == null ? status === 'PUBLISHED' : Boolean(value.refreshAllowed),
    blockingReasons: Array.isArray(value.blockingReasons)
      ? value.blockingReasons.map((item) => String(item || '')).filter(Boolean)
      : [],
    noOp: Boolean(value.noOp),
    hashVersion: value.hashVersion == null || value.hashVersion === '' ? null : Number(value.hashVersion),
    lastMaterializedAt: value.lastMaterializedAt || null,
  };
}

export function isPublishedPublic(publication) {
  return publication?.presence === 'PRESENT'
    && publication.status === 'PUBLISHED'
    && publication.visibility === 'PUBLIC'
    && Boolean(publication.publicationId);
}

export function publicJourneyPath(publication) {
  if (!isPublishedPublic(publication)) return null;
  return `/public/journeys/${publication.publicationId}`;
}

export function linkedJourneyPublicationId(publication) {
  return isPublishedPublic(publication) ? publication.publicationId : null;
}

/**
 * PD-PUB-01 UX gate：回傳缺少有效 locationId 的日子（第一個起算，完整列表供提示）。
 * Valid place＝非 CANCELLED 且有可用 locationId；僅摘要字串不算。
 */
export function findDaysMissingValidPlace(schedule) {
  const days = Array.isArray(schedule?.days) ? schedule.days : [];
  const missing = [];
  for (const day of days) {
    const activities = Array.isArray(day?.activities) ? day.activities : [];
    const hasPlace = activities.some((activity) => {
      if (!activity) return false;
      if (String(activity.status || '') === 'CANCELLED') return false;
      const locationId = activity.locationId == null ? '' : String(activity.locationId).trim();
      return /^[1-9]\d*$/.test(locationId);
    });
    if (!hasPlace) {
      missing.push({
        journeyDayId: day.journeyDayId == null ? null : String(day.journeyDayId),
        dayDate: day.date || null,
        displayOrder: Number(day.displayOrder) || 0,
        title: day.title || '',
      });
    }
  }
  return missing;
}

export function canPublish(publication) {
  if (!publication || publication.presence === NO_PUBLICATION) return true;
  if (!publication.statusKnown) return false;
  return publication.status === 'DRAFT' || publication.status === 'UNPUBLISHED';
}

export function canUnpublish(publication) {
  return publication?.presence === 'PRESENT'
    && publication.statusKnown
    && publication.status === 'PUBLISHED';
}

export function canRepublish(publication) {
  return publication?.presence === 'PRESENT'
    && publication.statusKnown
    && publication.status === 'UNPUBLISHED';
}

export function canSetPublic(publication) {
  return publication?.presence === 'PRESENT'
    && publication.statusKnown
    && publication.visibilityKnown
    && publication.status !== 'REMOVED'
    && publication.visibility !== 'PUBLIC';
}

export function publicationStatusCopy(publication) {
  if (!publication || publication.presence === NO_PUBLICATION) {
    return '這趟旅程目前尚未公開。';
  }
  if (!publication.statusKnown) {
    return '公開狀態無法辨識。目前無法調整公開設定。';
  }
  if (publication.status === 'REMOVED') {
    return '這趟旅程的公開設定已移除，無法再次公開。';
  }
  if (publication.status === 'UNPUBLISHED') {
    return '這趟旅程目前已停止分享。';
  }
  if (publication.status === 'DRAFT') {
    return '公開設定尚未完成。';
  }
  if (publication.status === 'PUBLISHED' && publication.visibility === 'PUBLIC') {
    return '這趟旅程目前公開中。';
  }
  if (publication.status === 'PUBLISHED') {
    return '這趟旅程已發布，但尚未設為公開可見。';
  }
  return '公開狀態無法辨識。目前無法調整公開設定。';
}

/** PD-EDIT-01：公開中編輯時告知快照尚未更新，需由擁有者明確更新公開版本。 */
export const PUBLISHED_EDIT_SNAPSHOT_NOTICE =
  '你的修改尚未更新到公開版本。目前公開頁面仍顯示上一次發布的內容。';

/** PD-EDIT-01：公開中離開未儲存編輯器時的加強提示。 */
export const PUBLISHED_EDIT_UNSAVED_LEAVE_MESSAGE =
  '這趟行程目前公開中。未儲存的修改將會消失，且公開頁也不會更新。確定要離開？';

export function isPublicationPublished(publication) {
  return publication?.presence === 'PRESENT'
    && publication.statusKnown
    && publication.status === 'PUBLISHED';
}

export function publicationVisibilityCopy(publication) {
  if (!publication || publication.presence === NO_PUBLICATION || !publication.visibility) {
    return '尚未設定可見對象';
  }
  if (!publication.visibilityKnown) {
    return '可見對象無法辨識';
  }
  const label = publicationVisibilityProductLabel(publication.visibility);
  return label ? `目前可見對象：${label}` : '可見對象無法辨識';
}

/** Guided-flow product presence (not domain status enums). */
export function guidedPublicationPresenceCopy(publication) {
  if (!publication || publication.presence === NO_PUBLICATION) {
    return '尚未分享';
  }
  if (!publication.statusKnown) {
    return '分享狀態無法辨識';
  }
  if (publication.status === 'PUBLISHED') {
    return '已分享';
  }
  if (publication.status === 'DRAFT') {
    return '分享設定進行中';
  }
  if (publication.status === 'UNPUBLISHED') {
    return '已停止分享';
  }
  if (publication.status === 'REMOVED') {
    return '分享設定已移除';
  }
  return '分享狀態無法辨識';
}

/** Planning page primary CTA label. */
export function planningPublishEntryLabel(publication, { requesterRole, lifecycleStatus } = {}) {
  const actions = ownerPublicationActions(publication, { requesterRole, lifecycleStatus });
  if (!actions.owner || actions.cancelled) return null;
  if (!publication || publication.presence === NO_PUBLICATION || canPublish(publication)) {
    return '分享行程';
  }
  if (publication.presence === 'PRESENT') {
    return '分享設定';
  }
  return null;
}

export function isGuidedPublicationSettingsMode(publication) {
  return publication?.presence === 'PRESENT'
    && publication.statusKnown
    && (publication.status === 'PUBLISHED' || publication.status === 'DRAFT');
}

/**
 * TRAVEL-V2-I18N：呈現層翻譯用 key（既有中文字串欄位保留給尚未遷移的頁面）。
 */
const SECTION = 'travel.publication.section';

function actionKeys(action, confirmCopy) {
  return {
    label: `${SECTION}.action.${action}`,
    confirmAction: `${SECTION}.confirmAction.${action}`,
    title: `${SECTION}.confirm.${confirmCopy}.title`,
    body: confirmCopy === 'unpublish' ? 'travel.publication.unpublish.body' : `${SECTION}.confirm.${confirmCopy}.body`,
  };
}

export function publicationStatusKey(publication) {
  if (!publication || publication.presence === NO_PUBLICATION) return `${SECTION}.status.NO_PUBLICATION`;
  if (!publication.statusKnown) return `${SECTION}.status.UNKNOWN`;
  if (publication.status === 'PUBLISHED' && publication.visibility === 'PUBLIC') return `${SECTION}.status.PUBLIC`;
  if (['REMOVED', 'UNPUBLISHED', 'DRAFT', 'PUBLISHED'].includes(publication.status)) {
    return `${SECTION}.status.${publication.status}`;
  }
  return `${SECTION}.status.UNKNOWN`;
}

/** 可見對象文案：{ key, labelKey? }；labelKey 由呈現層翻譯後代入 {label}。 */
export function publicationVisibilityKeys(publication) {
  if (!publication || publication.presence === NO_PUBLICATION || !publication.visibility) {
    return { key: `${SECTION}.visibility.notSet` };
  }
  if (!publication.visibilityKnown || !PUBLICATION_VISIBILITY_PRODUCT_LABELS[publication.visibility]) {
    return { key: `${SECTION}.visibility.unknown` };
  }
  return { key: `${SECTION}.visibility.current`, labelKey: `${SECTION}.visibility.label.${publication.visibility}` };
}

/**
 * 公開設定錯誤：回傳 { key } 或後端訊息 { text }（validation／預設／網路沿用既有優先順序）。
 */
export function publicationErrorDescriptor(kind, cause) {
  switch (kind) {
    case 'not-found':
    case 'journey-not-found':
    case 'forbidden':
    case 'conflict':
    case 'already-exists':
    case 'lifecycle-not-writable':
    case 'invalid-lifecycle':
    case 'ownership-drift':
    case 'day-missing-place':
      return { key: `${SECTION}.error.${kind}` };
    case 'custom-location-not-public':
      return { key: 'travel.publication.refresh.error.customLocation' };
    case 'validation': {
      const text = cause?.raw?.message || cause?.message;
      return text ? { text } : { key: `${SECTION}.error.validation` };
    }
    case 'unauthorized':
      return { key: 'travel.publication.story.error.unauthorized' };
    case 'network':
      return cause?.message ? { text: cause.message } : { key: 'travel.publication.story.error.network' };
    default: {
      const text = cause?.raw?.message || cause?.message;
      return text ? { text } : { key: `${SECTION}.error.generic` };
    }
  }
}

export function ownerPublicationActions(publication, { requesterRole, lifecycleStatus } = {}) {
  const owner = requesterRole === 'OWNER';
  const cancelled = lifecycleStatus === 'CANCELLED';
  const known = Boolean(publication?.statusKnown && publication?.visibilityKnown);
  const mutable = owner && !cancelled && known && publication?.status !== 'REMOVED';

  let primary = null;
  if (mutable && canPublish(publication)) {
    const isRepublish = publication?.presence === 'PRESENT' && publication.status === 'UNPUBLISHED';
    primary = {
      kind: 'MAKE_PUBLIC',
      label: isRepublish ? '重新公開' : '公開旅程',
      confirm: isRepublish ? REPUBLISH_CONFIRM_COPY : PUBLISH_CONFIRM_COPY,
      keys: actionKeys(isRepublish ? 'REPUBLISH' : 'MAKE_PUBLIC', isRepublish ? 'republish' : 'publish'),
    };
  } else if (mutable && publication?.status === 'PUBLISHED' && canSetPublic(publication)) {
    primary = {
      kind: 'SET_PUBLIC',
      label: '設為公開',
      confirm: PUBLISH_CONFIRM_COPY,
      keys: actionKeys('SET_PUBLIC', 'publish'),
    };
  }

  const secondary = mutable && canUnpublish(publication)
    ? { kind: 'UNPUBLISH', label: '停止分享', confirm: UNPUBLISH_CONFIRM_COPY, keys: actionKeys('UNPUBLISH', 'unpublish') }
    : null;

  return {
    owner,
    cancelled,
    mutable,
    primary,
    secondary,
    cancelledHint: cancelled ? '已取消的旅程無法公開。' : '',
    cancelledHintKey: cancelled ? `${SECTION}.cancelledHint` : '',
  };
}

export function publicationErrorKind(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || '');
  const message = String(cause?.raw?.message || cause?.message || '');
  if (key === 'travel.publication.not.found') return 'not-found';
  if (key === 'travel.journey.not.found') return 'journey-not-found';
  if (key === 'travel.publication.permission.denied') return 'forbidden';
  if (key === 'travel.publication.concurrent.modification') return 'conflict';
  if (key === 'travel.publication.already.exists') return 'already-exists';
  if (key === 'travel.publication.lifecycle.not.writable') return 'lifecycle-not-writable';
  if (key === 'travel.publication.invalid.lifecycle') return 'invalid-lifecycle';
  if (key === 'travel.publication.ownership.drift') return 'ownership-drift';
  if (key === 'travel.publication.day.missing.place' || code === 95104) return 'day-missing-place';
  if (key === 'travel.publication.custom.location.not.public' || code === 95106) return 'custom-location-not-public';
  if (key === 'travel.publication.validation.failed' || code === 95100) return 'validation';
  if (code === 95101) return 'forbidden';
  if (code === 90003 || code === 90005 || code === 90006
    || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 95102 && key.includes('publication')) return 'not-found';
  if (code === 95102 && key.includes('journey')) return 'journey-not-found';
  if (!cause?.raw && /network|timeout|failed to fetch|gateway/i.test(message)) return 'network';
  if (!cause?.code && !cause?.raw) return 'network';
  return 'error';
}

export function publicationErrorMessage(kind, cause) {
  switch (kind) {
    case 'not-found':
      return '找不到公開設定。';
    case 'journey-not-found':
      return '找不到這趟旅程。';
    case 'forbidden':
      return '你沒有權限調整這趟旅程的公開設定。';
    case 'conflict':
      return '公開設定剛剛有新的變更，已重新載入最新狀態。';
    case 'already-exists':
      return '這趟旅程已有公開設定，已重新載入最新狀態。';
    case 'lifecycle-not-writable':
      return '目前旅程狀態無法調整公開設定。';
    case 'invalid-lifecycle':
      return '目前公開狀態無法執行這個操作。';
    case 'ownership-drift':
      return '旅程擁有者資料已變更，請重新整理後再試。';
    case 'day-missing-place':
      return '每一天都需要至少一個有效地點才能分享。請先補上缺少地點的日子。';
    case 'custom-location-not-public':
      return CUSTOM_LOCATION_NOT_PUBLIC_MESSAGE;
    case 'validation':
      return cause?.raw?.message || cause?.message || '公開設定資料不正確。';
    case 'unauthorized':
      return '請重新登入後再試。';
    case 'network':
      return cause?.message || '網路連線失敗，請稍後再試。';
    default:
      return cause?.raw?.message || cause?.message || '公開設定暫時無法完成。';
  }
}

export function isPublicationAbsentError(cause) {
  return publicationErrorKind(cause) === 'not-found';
}

export function publicationRefreshPresentation(publication) {
  if (!publication || publication.presence !== 'PRESENT' || publication.status !== 'PUBLISHED') {
    return 'UNPUBLISHED';
  }
  const dirty = publication.dirtyState || 'UNKNOWN';
  if (dirty === 'CHANGED' && publication.refreshAllowed === false) return 'PUBLISHED_CHANGED_BLOCKED';
  if (dirty === 'UNCHANGED' && publication.updateAvailable !== true) return 'PUBLISHED_UNCHANGED';
  if (dirty === 'CHANGED') return 'PUBLISHED_CHANGED';
  return 'PUBLISHED_UNKNOWN';
}

export function positiveRowVersion(value) {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return value;
  if (typeof value === 'string' && /^[1-9]\d{0,15}$/.test(value)) {
    const parsed = Number(value);
    if (Number.isSafeInteger(parsed) && parsed > 0) return parsed;
  }
  return null;
}

const BLOCKING_REASON_KEYS = {
  NOT_PUBLISHED: 'travel.publication.refresh.reason.state',
  DAY_MISSING_PLACE: 'travel.publication.refresh.reason.day',
  MISSING_PLACE: 'travel.publication.refresh.reason.day',
  CUSTOM_LOCATION: 'travel.publication.refresh.reason.customLocation',
  CUSTOM_LOCATION_NOT_PUBLIC: 'travel.publication.refresh.reason.customLocation',
  STATE_CHANGED: 'travel.publication.refresh.reason.changed',
  STALE: 'travel.publication.refresh.reason.reload',
};

export function blockingReasonCopy(reason, locale = 'zh_TW') {
  const key = BLOCKING_REASON_KEYS[String(reason || '').trim()];
  if (!key) return getPublicationRefreshMessage(locale, 'travel.publication.refresh.reason.generic');
  return getPublicationRefreshMessage(locale, key);
}

export function refreshErrorPresentation(cause, locale = 'zh_TW') {
  const httpStatus = Number(cause?.httpStatus || 0);
  const key = String(cause?.raw?.key || '');
  const code = cause?.code ?? cause?.raw?.code;
  const copy = (messageKey) => getPublicationRefreshMessage(locale, messageKey);
  if (httpStatus === 401 || code === 90003 || code === 90005 || code === 90006
    || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return { kind: 'unauthorized', message: '請重新登入後再試。', messageKey: 'travel.publication.story.error.unauthorized', reload: false };
  }
  if (httpStatus === 403 || key === 'travel.publication.permission.denied' || key === 'travel.publication.forbidden') {
    return { kind: 'forbidden', message: copy('travel.publication.refresh.error.forbidden'), messageKey: 'travel.publication.refresh.error.forbidden', reload: false };
  }
  if (httpStatus === 404 || key === 'travel.publication.not.found' || key === 'travel.journey.not.found') {
    return { kind: 'not-found', message: copy('travel.publication.refresh.error.notFound'), messageKey: 'travel.publication.refresh.error.notFound', reload: false };
  }
  if (key === 'travel.publication.refresh.stale' || (httpStatus === 409 && key.includes('stale'))) {
    return { kind: 'stale', message: copy('travel.publication.refresh.error.stale'), messageKey: 'travel.publication.refresh.error.stale', reload: true };
  }
  if (key === 'travel.publication.refresh.not.published') {
    return { kind: 'lifecycle', message: copy('travel.publication.refresh.error.lifecycle'), messageKey: 'travel.publication.refresh.error.lifecycle', reload: true };
  }
  if (httpStatus === 409 && (key.includes('concurrent') || key === 'travel.publication.concurrent.modification')) {
    return { kind: 'concurrent', message: copy('travel.publication.refresh.error.concurrent'), messageKey: 'travel.publication.refresh.error.concurrent', reload: true };
  }
  if (httpStatus === 409) {
    return { kind: 'lifecycle', message: copy('travel.publication.refresh.error.lifecycle'), messageKey: 'travel.publication.refresh.error.lifecycle', reload: true };
  }
  if (httpStatus === 422 && (key.includes('custom') || key.includes('CUSTOM_LOCATION'))) {
    return { kind: 'custom-location', message: copy('travel.publication.refresh.error.customLocation'), messageKey: 'travel.publication.refresh.error.customLocation', reload: false };
  }
  if (key === 'travel.publication.refresh.ineligible' || code === 95105
    || key === 'travel.publication.day.missing.place' || key.includes('place')) {
    return { kind: 'day', message: copy('travel.publication.refresh.error.day'), messageKey: 'travel.publication.refresh.error.day', reload: false };
  }
  if (httpStatus === 422) {
    return { kind: 'eligibility', message: copy('travel.publication.refresh.error.eligibility'), messageKey: 'travel.publication.refresh.error.eligibility', reload: false };
  }
  if (httpStatus === 429) {
    return { kind: 'rate', message: copy('travel.publication.refresh.error.rate'), messageKey: 'travel.publication.refresh.error.rate', reload: false };
  }
  if (httpStatus === 400 || key === 'travel.publication.refresh.invalid') {
    return { kind: 'invalid', message: copy('travel.publication.refresh.error.invalid'), messageKey: 'travel.publication.refresh.error.invalid', reload: true };
  }
  return { kind: 'generic', message: copy('travel.publication.refresh.error.generic'), messageKey: 'travel.publication.refresh.error.generic', reload: true };
}

export function buildRefreshRequest(versions) {
  return {
    expectedJourneyRowVersion: positiveRowVersion(versions?.journeyRowVersion),
    expectedItineraryRowVersion: positiveRowVersion(versions?.itineraryRowVersion),
    expectedPublicationRowVersion: positiveRowVersion(versions?.publicationRowVersion),
    ...(Number.isInteger(versions?.storyRevision) && versions.storyRevision >= 0
      ? { expectedStoryRevision: versions.storyRevision }
      : {}),
    confirm: true,
  };
}

/**
 * Sequential MVP publish: create DRAFT (if needed) → PUBLIC visibility → PUBLISHED.
 * Never parallel. Each step uses the previous server rowVersion.
 * CAS conflict must stop immediately (caller reloads).
 */
export async function orchestrateMakePublic(options) {
  return orchestratePublishWithVisibility({ ...options, visibility: 'PUBLIC' });
}

/**
 * Sequential publish with chosen visibility (existing APIs only).
 * create DRAFT (if needed) → visibility → PUBLISHED.
 */
export async function orchestratePublishWithVisibility({
  journeyId,
  current,
  visibility = 'PUBLIC',
  createDraft,
  changeVisibility,
  publish,
  getByJourney,
  shouldAbort = () => false,
}) {
  const targetVisibility = PUBLICATION_VISIBILITIES.includes(visibility) ? visibility : 'PUBLIC';
  let state = current?.presence === 'PRESENT' ? current : emptyPublication(journeyId);

  if (state.presence !== 'PRESENT') {
    try {
      state = normalizeJourneyPublication(await createDraft(journeyId), journeyId);
    } catch (cause) {
      if (publicationErrorKind(cause) !== 'already-exists') throw cause;
      state = normalizeJourneyPublication(await getByJourney(journeyId), journeyId);
    }
    if (shouldAbort()) return { aborted: true, publication: state };
  }

  if (state.status === 'PUBLISHED' && state.visibility === targetVisibility) {
    return { aborted: false, publication: state };
  }

  if (state.visibility !== targetVisibility) {
    state = normalizeJourneyPublication(await changeVisibility(journeyId, {
      visibility: targetVisibility,
      expectedRowVersion: state.rowVersion,
      confirm: true,
    }), journeyId);
    if (shouldAbort()) return { aborted: true, publication: state };
  }

  if (state.status !== 'PUBLISHED') {
    state = normalizeJourneyPublication(await publish(journeyId, {
      expectedRowVersion: state.rowVersion,
      confirm: true,
    }), journeyId);
  }

  return { aborted: false, publication: state };
}
